<template>
  <div class="admin-page referral-reviews">
    <admin-page-header
      eyebrow="Referral"
      title="Referral İnceleme"
      description="Fraud review, hard cap ve manuel inceleme gerektiren referral kayıtlarını yönetin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading"
          @click="fetchReviews"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="successMessage"
      dense
      outlined
      type="success"
      class="mb-4"
    >
      {{ successMessage }}
    </v-alert>

    <admin-metric-strip
      :items="metrics.map(item => ({ ...item, value: formatNumber(item.value) }))"
      :loading="loading"
    />
    <v-dialog
      v-model="actionDialog"
      max-width="540"
      persistent
    >
      <admin-confirm-dialog
        :title="pendingAction === 'approve' ? 'Referral onayı' : pendingAction === 'reject' ? 'Referral reddi' : 'İnceleme kararı'"
        :description="pendingAction === 'reject' ? `#${actionTargetId} reddedilecek. Bu davet için verilen ve daha önce geri alınmamış ödül puanı otomatik geri alınır. Kullanılmış puan nedeniyle bakiye yetersizse işlem kaydedilmez ve manuel mutabakat gerekir.` : `#${actionTargetId} kaydı için karar kaydedilecek. İlişkili ödül uygunluğu bu karardan etkilenebilir.`"
        :item-id="actionTargetId"
        :note="actionNote"
        :save="performAction"
        :close="() => { actionDialog = false }"
      />
    </v-dialog>

    <v-card
      class="admin-table-card my-6"
      outlined
    >
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model.trim="filters.search"
              outlined
              dense
              clearable
              hide-details
              label="Ara"
              placeholder="Kod, email, telefon veya kullanıcı"
              @keydown.enter.prevent="applyFilters"
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              outlined
              dense
              clearable
              hide-details
              label="Durum"
            ></v-select>
          </v-col>

          <v-col
            cols="12"
            md="2"
          >
            <v-select
              v-model="filters.size"
              :items="pageSizeOptions"
              outlined
              dense
              hide-details
              label="Sayfa boyutu"
            ></v-select>
          </v-col>

          <v-col
            cols="12"
            md="3"
            class="d-flex align-center justify-md-end"
          >
            <v-switch
              v-model="filters.reviewOnly"
              dense
              inset
              :disabled="filters.status === 'ACCOUNT_DELETED'"
              hide-details
              label="Sadece inceleme kuyruğu"
            ></v-switch>
          </v-col>
        </v-row>

        <div class="d-flex align-center justify-space-between flex-wrap mt-4">
          <div class="text-caption text--secondary">
            {{ formatNumber(totalItems) }} referral kaydı
          </div>

          <div class="d-flex align-center flex-wrap">
            <v-btn
              text
              small
              color="primary"
              class="mr-2"
              @click="clearFilters"
            >
              Filtreleri Temizle
            </v-btn>
            <v-btn
              color="primary"
              small
              :loading="loading"
              @click="applyFilters"
            >
              Uygula
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col
        cols="12"
        lg="8"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>İnceleme kayıtları</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-chip
              small
              outlined
              color="primary"
            >
              Sayfa {{ displayPage }}
            </v-chip>
          </v-toolbar>

          <v-data-table
            :headers="headers"
            :items="reviews"
            :loading="loading"
            :items-per-page="Number(filters.size)"
            hide-default-footer
            class="admin-data-table elevation-0"
            @click:row="selectReview"
          >
            <template v-slot:item.code="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ item.code || `#${item.id}` }}
                </div>
                <div class="text-caption text--secondary">
                  {{ formatDateTime(item.createdAt) }}
                </div>
              </div>
            </template>

            <template v-slot:item.referrer="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ userId(item.referrer) }} · {{ userName(item.referrer) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ userEmail(item.referrer) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ userPhone(item.referrer) }}
                </div>
              </div>
            </template>

            <template v-slot:item.referred="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ userId(item.referred) }} · {{ userName(item.referred) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ userEmail(item.referred) || item.referredEmailFingerprint || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.referredPhone || userPhone(item.referred) }}
                </div>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <div class="d-flex flex-column align-start">
                <v-chip
                  x-small
                  label
                  :color="statusColor(item.status)"
                  :class="{ 'referral-reviews__status--deleted': item.status === 'ACCOUNT_DELETED' }"
                  dark
                >
                  {{ item.statusLabel || statusLabel(item.status) }}
                </v-chip>
                <span
                  v-if="item.reviewReason"
                  class="text-caption text--secondary mt-1"
                >
                  {{ item.reviewReason }}
                </span>
              </div>
            </template>

            <template v-slot:item.reward="{ item }">
              <div class="referral-reviews__reward-cell">
                <div class="d-flex align-center justify-space-between">
                  <span class="font-weight-medium">
                    {{ formatAdvp(netReward(item)) }}
                  </span>
                  <span class="text-caption text--secondary">
                    / 5.000 ADVP
                  </span>
                </div>
                <v-progress-linear
                  :value="rewardProgress(item)"
                  height="6"
                  rounded
                  color="primary"
                  background-color="grey lighten-3"
                  class="mt-2"
                ></v-progress-linear>
                <div
                  v-if="Number(item.rewardReversedAmount) > 0"
                  class="text-caption error--text mt-1"
                >
                  {{ formatAdvp(item.rewardReversedAmount) }} geri alındı
                </div>
              </div>
            </template>

            <template v-slot:item.flags="{ item }">
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="item.fraudReviewFlag"
                  x-small
                  color="warning"
                  label
                  class="mr-1 mb-1"
                >
                  Risk incelemesi
                </v-chip>
                <v-chip
                  v-if="item.manualReviewRequired"
                  x-small
                  color="info"
                  label
                  class="mr-1 mb-1"
                >
                  Manuel
                </v-chip>
                <v-chip
                  v-if="item.countedTowardCap"
                  x-small
                  :color="Number(item.rewardReversedAmount) > 0 ? 'grey' : 'success'"
                  label
                  class="mr-1 mb-1"
                  title="Geçmiş limit sayacı korunur; bu etiket mevcut ödül bakiyesi değildir."
                >
                  {{ Number(item.rewardReversedAmount) > 0 ? 'Geçmiş limit kaydı' : 'Limite dahil' }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="primary"
                aria-label="Referral kaydını incele"
                title="İncele"
                @click.stop="selectReview(item)"
              >
                <v-icon small>
                  {{ icons.mdiEyeOutline }}
                </v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="py-8 text-center text--secondary">
                İnceleme kaydı bulunamadı.
              </div>
            </template>
          </v-data-table>

          <div class="d-flex align-center justify-space-between pa-4 flex-wrap">
            <v-btn
              outlined
              small
              :disabled="page <= 0 || loading"
              @click="goToPage(page - 1)"
            >
              Önceki
            </v-btn>
            <div class="text-caption text--secondary my-2">
              {{ formatNumber(totalItems) }} kayıttan {{ formatNumber(reviews.length) }} kayıt gösteriliyor
            </div>
            <v-btn
              outlined
              small
              :disabled="!hasNextPage || loading"
              @click="goToPage(page + 1)"
            >
              Sonraki
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="4"
      >
        <v-card
          class="admin-table-card referral-reviews__detail"
          outlined
        >
          <v-card-title class="align-start">
            <div>
              <div class="text-h6">
                İnceleme Detayı
              </div>
              <div class="text-caption text--secondary">
                {{ selectedReview ? reviewCode(selectedReview) : 'Bir kayıt seçin' }}
              </div>
            </div>
          </v-card-title>

          <v-card-text v-if="selectedReview">
            <div class="referral-reviews__stage mb-4">
              <div
                v-for="stage in selectedStages"
                :key="stage.label"
                class="referral-reviews__stage-row"
              >
                <v-icon
                  small
                  :color="stage.done ? 'success' : 'grey'"
                >
                  {{ stage.done ? icons.mdiCheckCircleOutline : icons.mdiCheckboxBlankCircleOutline }}
                </v-icon>
                <div>
                  <div class="font-weight-medium">
                    {{ stage.label }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ stage.meta }}
                  </div>
                </div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <dl class="referral-reviews__facts">
              <div>
                <dt>Referrer</dt>
                <dd>{{ userId(selectedReview.referrer) }} · {{ userName(selectedReview.referrer) }} · {{ userEmail(selectedReview.referrer) }}</dd>
                <dd>{{ userPhone(selectedReview.referrer) }}</dd>
                <dd class="text-caption text--secondary">
                  {{ selectedReview.referrer && selectedReview.referrer.phoneVerifiedAt ? 'Telefon doğrulandı' : 'Telefon doğrulanmadı' }}
                </dd>
              </div>
              <div>
                <dt>Davetli</dt>
                <dd>{{ userId(selectedReview.referred) }} · {{ userName(selectedReview.referred) }} · {{ userEmail(selectedReview.referred) }}</dd>
                <dd>{{ selectedReview.referredPhone || userPhone(selectedReview.referred) }}</dd>
                <dd class="text-caption text--secondary">
                  {{ selectedReview.referred && selectedReview.referred.phoneVerifiedAt ? 'Telefon doğrulandı' : 'Telefon doğrulanmadı' }}
                </dd>
              </div>
              <div>
                <dt>Confirmed kazanç</dt>
                <dd>{{ formatCurrency(selectedReview.totalConfirmedEarningsAmount) }}</dd>
              </div>
              <div>
                <dt>Net ödül</dt>
                <dd>{{ formatAdvp(netReward(selectedReview)) }} / 5.000 ADVP</dd>
              </div>
              <div v-if="Number(selectedReview.rewardReversedAmount) > 0">
                <dt>Geçmişte verilen / geri alınan</dt>
                <dd>{{ formatAdvp(selectedReview.rewardUnlockedAmount) }} / {{ formatAdvp(selectedReview.rewardReversedAmount) }}</dd>
              </div>
              <div v-if="selectedReview.reviewDecision">
                <dt>Son karar</dt>
                <dd>{{ selectedReview.reviewDecision }} · {{ selectedReview.reviewedBy || '-' }}</dd>
              </div>
              <div v-if="selectedReview.reviewNote">
                <dt>Not</dt>
                <dd>{{ selectedReview.reviewNote }}</dd>
              </div>
            </dl>

            <v-textarea
              v-model.trim="reviewNote"
              outlined
              dense
              rows="3"
              auto-grow
              :disabled="selectedReviewIsAccountDeleted"
              label="İnceleme notu"
              placeholder="Karar notunu yaz"
              class="mt-4"
            ></v-textarea>

            <v-alert
              v-if="selectedReviewIsAccountDeleted"
              dense
              text
              type="info"
            >
              Hesap silindiği için referral kaydı donduruldu; inceleme aksiyonu uygulanamaz.
            </v-alert>

            <div class="d-flex flex-wrap">
              <v-btn
                color="success"
                small
                class="mr-2 mb-2"
                :loading="actionLoading === 'approve'"
                :disabled="Boolean(actionLoading) || selectedReviewIsAccountDeleted"
                @click="runAction('approve')"
              >
                Onayla
              </v-btn>
              <v-btn
                color="warning"
                small
                outlined
                class="mr-2 mb-2"
                :loading="actionLoading === 'hold'"
                :disabled="Boolean(actionLoading) || selectedReviewIsAccountDeleted"
                @click="runAction('hold')"
              >
                İncelemede Tut
              </v-btn>
              <v-btn
                color="error"
                small
                outlined
                class="mb-2"
                :loading="actionLoading === 'reject'"
                :disabled="Boolean(actionLoading) || selectedReviewIsAccountDeleted"
                @click="runAction('reject')"
              >
                Reddet
              </v-btn>
            </div>
          </v-card-text>

          <v-card-text
            v-else
            class="text--secondary"
          >
            Soldaki listeden fraud veya cap incelemesi gereken bir referral seç.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'
import * as icons from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog.vue'
import { resolveApiErrorMessage } from '@/utils/apiErrors'

const TOTAL_REWARD_AMOUNT = 5000

export default {
  components: {
    AdminPageHeader,
    AdminMetricStrip,
    AdminConfirmDialog,
  },
  data: () => ({
    actionDialog: false,
    pendingAction: '',
    actionTargetId: null,
    actionNote: '',
    icons,
    loading: false,
    actionLoading: '',
    errorMessage: '',
    successMessage: '',
    page: 0,
    totalItems: 0,
    totalPages: 0,
    reviews: [],
    selectedReview: null,
    reviewNote: '',
    summary: {
      pendingReviewCount: 0,
      fraudReviewCount: 0,
      capReviewCount: 0,
      rejectedCount: 0,
      manuallyApprovedCount: 0,
      totalInviteCount: 0,
    },
    filters: {
      search: '',
      status: '',
      reviewOnly: true,
      size: 25,
    },
    pageSizeOptions: [10, 25, 50, 100],
    statusOptions: [
      { text: 'Risk incelemesi', value: 'FRAUD_REVIEW' },
      { text: 'Manuel onay', value: 'MANUALLY_APPROVED' },
      { text: 'Uygun değil', value: 'REJECTED' },
      { text: 'Hesap silindi', value: 'ACCOUNT_DELETED' },
      { text: 'Doğrulandı', value: 'VERIFIED' },
      { text: 'Ödüllendirildi', value: 'REWARDED' },
      { text: 'Email bekleniyor', value: 'PENDING_EMAIL' },
      { text: 'WhatsApp bekleniyor', value: 'PENDING_WHATSAPP' },
    ],
    headers: [
      { text: 'Kod', value: 'code', width: 130 },
      { text: 'Referrer', value: 'referrer', width: 190 },
      { text: 'Davetli', value: 'referred', width: 220 },
      { text: 'Durum', value: 'status', width: 170 },
      {
        text: 'Ödül',
        value: 'reward',
        width: 170,
        sortable: false,
      },
      {
        text: 'Sinyaller',
        value: 'flags',
        width: 170,
        sortable: false,
      },
      {
        text: '',
        value: 'actions',
        width: 56,
        sortable: false,
      },
    ],
  }),
  computed: {
    requestOptions() {
      return {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      }
    },
    displayPage() {
      return this.page + 1
    },
    hasNextPage() {
      if (this.totalPages > 0) {
        return this.page + 1 < this.totalPages
      }

      return (this.page + 1) * Number(this.filters.size) < this.totalItems
    },
    selectedReviewIsAccountDeleted() {
      return this.selectedReview?.status === 'ACCOUNT_DELETED'
    },
    metrics() {
      return [
        { label: 'İnceleme Kuyruğu', value: this.summary.pendingReviewCount },
        { label: 'Risk İncelemesi', value: this.summary.fraudReviewCount },
        { label: 'Limit İncelemesi', value: this.summary.capReviewCount },
        { label: 'Manuel Onay', value: this.summary.manuallyApprovedCount },
        { label: 'Reddedilen', value: this.summary.rejectedCount },
        { label: 'Toplam Referral', value: this.summary.totalInviteCount },
      ]
    },
    selectedStages() {
      if (!this.selectedReview) return []

      const review = this.selectedReview
      const { referred } = review
      const activeAccount = referred?.active === true && review.status !== 'ACCOUNT_DELETED'
      const emailVerified = Boolean(review.emailVerifiedAt)
      const phoneVerified = activeAccount && Boolean(referred?.phoneVerifiedAt)
      const verificationComplete = emailVerified && phoneVerified
      let verificationMeta

      if (referred?.active === false || review.status === 'ACCOUNT_DELETED') {
        verificationMeta = 'Hesap silindi; güncel doğrulama yok'
      } else if (!referred || referred.active === undefined) {
        verificationMeta = 'Güncel doğrulama bilgisi yok'
      } else if (verificationComplete) {
        verificationMeta = this.formatDateTime(Math.max(Number(review.emailVerifiedAt), Number(referred.phoneVerifiedAt)))
      } else if (!emailVerified && !phoneVerified) {
        verificationMeta = 'E-posta ve telefon doğrulaması bekleniyor'
      } else {
        verificationMeta = emailVerified ? 'Telefon doğrulaması bekleniyor' : 'E-posta doğrulaması bekleniyor'
      }

      if (!verificationComplete && review.phoneVerifiedAt) {
        verificationMeta += ` · Geçmiş doğrulama: ${this.formatDateTime(review.phoneVerifiedAt)}`
      }

      return [
        {
          label: 'Davetli e-posta + telefon doğrulama',
          done: verificationComplete,
          meta: verificationMeta,
        },
        {
          label: '100 TL confirmed kazanç',
          done: Boolean(this.selectedReview.secondStageRewardedAt || this.selectedReview.confirmed100ReachedAt),
          meta: this.selectedReview.secondStageRewardedAt || this.selectedReview.confirmed100ReachedAt
            ? this.formatDateTime(this.selectedReview.secondStageRewardedAt || this.selectedReview.confirmed100ReachedAt)
            : 'Ek 2.000 ADVP unlock bekliyor',
        },
        {
          label: '1.000 TL confirmed kazanç',
          done: Boolean(this.selectedReview.thirdStageRewardedAt || this.selectedReview.rewardGrantedAt),
          meta: this.selectedReview.thirdStageRewardedAt || this.selectedReview.rewardGrantedAt
            ? this.formatDateTime(this.selectedReview.thirdStageRewardedAt || this.selectedReview.rewardGrantedAt)
            : 'Son 2.000 ADVP unlock bekliyor',
        },
      ]
    },
  },
  watch: {
    'filters.size': function pageSizeChanged() {
      this.applyFilters()
    },
    'filters.reviewOnly': function reviewOnlyChanged() {
      this.applyFilters()
    },
    'filters.status': function statusChanged(status) {
      if (status === 'ACCOUNT_DELETED' && this.filters.reviewOnly) {
        this.filters.reviewOnly = false

        return
      }
      this.applyFilters()
    },
  },
  mounted() {
    this.fetchReviews()
  },
  methods: {
    referralUrl(path) {
      return `${process.env.VUE_APP_BASE_API_URL}admin/referrals${path}`
    },
    async fetchReviews() {
      this.loading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(this.referralUrl('/reviews'), {
          ...this.requestOptions,
          params: this.buildParams(),
        })
        const payload = response?.data?.data || response?.data || {}
        this.reviews = payload.items || payload.content || []
        this.summary = {
          ...this.summary,
          ...(payload.summary || {}),
        }
        this.totalItems = Number(payload.totalElements || payload.totalItems || payload.total || this.reviews.length || 0)
        this.totalPages = Number(payload.totalPages || 0)

        if (this.selectedReview) {
          this.selectedReview = this.reviews.find(item => item.id === this.selectedReview.id) || this.reviews[0] || null
        } else {
          this.selectedReview = this.reviews[0] || null
        }
        this.reviewNote = this.selectedReview?.reviewNote || ''
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Referral inceleme kayıtları yüklenemedi.'
        console.error('Referral inceleme kayıtları yüklenirken hata oluştu:', error)
      } finally {
        this.loading = false
      }
    },
    buildParams() {
      const params = {
        page: this.page,
        size: this.filters.size,
        reviewOnly: this.filters.reviewOnly,
      }

      if (this.filters.status) params.status = this.filters.status
      if (this.filters.search) params.search = this.filters.search

      return params
    },
    applyFilters() {
      this.page = 0
      this.fetchReviews()
    },
    clearFilters() {
      this.filters = {
        search: '',
        status: '',
        reviewOnly: true,
        size: 25,
      }
      this.applyFilters()
    },
    goToPage(nextPage) {
      this.page = Math.max(0, nextPage)
      this.fetchReviews()
    },
    selectReview(item) {
      this.selectedReview = item
      this.reviewNote = item?.reviewNote || ''
      this.successMessage = ''
      this.errorMessage = ''
    },
    runAction(action) {
      if (!this.selectedReview?.id || this.selectedReviewIsAccountDeleted || this.actionLoading) return
      if (action === 'reject' && !this.reviewNote.trim()) {
        this.errorMessage = 'Ret gerekçesini inceleme notuna yazın.'

        return
      }
      this.pendingAction = action; this.actionTargetId = this.selectedReview.id; this.actionNote = this.reviewNote; this.actionDialog = true
    },
    async performAction() {
      if (!this.actionTargetId || this.actionLoading) return false
      const action = this.pendingAction

      this.actionLoading = action
      this.errorMessage = ''
      this.successMessage = ''

      try {
        await axios.post(
          this.referralUrl(`/reviews/${this.actionTargetId}/${action}`),
          { note: this.actionNote },
          this.requestOptions,
        )
        this.successMessage = this.actionLabel(action)
        await this.fetchReviews()

        return true
      } catch (error) {
        const message = resolveApiErrorMessage(error, 'Referral inceleme kararı kaydedilemedi.')
        if (error?.response?.status === 409) await this.fetchReviews()
        throw new Error(message)
      } finally {
        this.actionLoading = ''
      }
    },
    actionLabel(action) {
      if (action === 'approve') return 'Referral incelemesi onaylandı.'
      if (action === 'reject') return 'Referral incelemesi reddedildi.'

      return 'Referral incelemesi açık bırakıldı.'
    },
    userId(user) {
      return user && user.id ? `#${user.id}` : '-'
    },
    userName(user) {
      return user?.fullName || (user?.id ? `#${user.id}` : '-')
    },
    userEmail(user) {
      return user?.email || '-'
    },
    userPhone(user) {
      return user?.phone || user?.phoneMasked || '-'
    },
    reviewCode(item) {
      return item?.code || `#${item?.id || '-'}`
    },
    statusLabel(status) {
      const match = this.statusOptions.find(item => item.value === status)

      return match?.text || status || '-'
    },
    statusColor(status) {
      const colorMap = {
        FRAUD_REVIEW: 'warning',
        MANUALLY_APPROVED: 'success',
        REJECTED: 'error',
        ACCOUNT_DELETED: '#475569',
        VERIFIED: 'info',
        REWARDED: 'success',
        PENDING_EMAIL: 'grey',
        PENDING_WHATSAPP: 'grey',
      }

      return colorMap[status] || 'primary'
    },
    rewardProgress(item) {
      const amount = this.netReward(item)

      return Math.max(0, Math.min(100, (amount / TOTAL_REWARD_AMOUNT) * 100))
    },
    netReward(item) {
      return Math.max(0, Number(item?.rewardNetAmount ?? (Number(item?.rewardUnlockedAmount || 0) - Number(item?.rewardReversedAmount || 0))))
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatAdvp(value) {
      return `${this.formatNumber(value)} ADVP`
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
      }).format(Number(value || 0))
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parsed = new Date(Number(value))
      if (Number.isNaN(parsed.getTime())) return '-'

      return parsed.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.referral-reviews__status--deleted {
  background-color: #475569 !important;
  border: 1px solid #334155 !important;
  color: #fff !important;
  font-weight: 600;
  opacity: 1;
}

.referral-reviews__metric {
  padding: 18px;
  min-height: 104px;
}

.referral-reviews__reward-cell {
  min-width: 136px;
}

.referral-reviews__detail {
  position: sticky;
  top: 88px;
}

.referral-reviews__stage {
  display: grid;
  gap: 14px;
}

.referral-reviews__stage-row {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
}

.referral-reviews__facts {
  display: grid;
  gap: 12px;
  margin: 0;

  div {
    display: grid;
    gap: 2px;
  }

  dt {
    font-size: 12px;
    color: rgba(100, 116, 139, 0.9);
  }

  dd {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }
}
</style>
